import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BenefitIcon } from "@/components/BenefitIcon";
import { DemoBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, ShieldCheck, Users, ClipboardCheck, Loader2, PartyPopper,
} from "lucide-react";

const STEPS = ["Choose coverage", "Confirm household", "Review & submit"];

export default function Enroll() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [selectedTier, setSelectedTier] = useState(null);
  const [coveredIds, setCoveredIds] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/products/${id}`), api.get(`/eligibility/${id}`)])
      .then(([p, e]) => {
        setProduct(p.data);
        setEligibility(e.data);
        const eligible = e.data.members.filter((m) => m.eligible && m.member_id).map((m) => m.member_id);
        setCoveredIds(eligible);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-6 sm:p-12 max-w-3xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;
  if (!product) return null;

  const options = product.coverage_options || [];
  const selectedOption = options[selectedTier];

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/enrollments/elect", {
        product_id: product.id,
        election: "enroll",
        coverage_tier: selectedOption?.tier,
        coverage_amount: selectedOption?.coverage_amount ?? null,
        covered_member_ids: coveredIds,
      });
      const { data } = await api.post("/enrollments/submit", { acknowledged: true });
      setDone(data);
      toast.success("Enrollment submitted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="p-6 sm:p-12 max-w-2xl mx-auto animate-rise">
        <div className="bg-white rounded-2xl border border-mist-200 p-10 text-center" data-testid="enrollment-confirmation">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
            <PartyPopper className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-display text-3xl font-bold text-navy-900">You're enrolled!</h1>
          <p className="text-muted-foreground mt-2">Your elections have been submitted successfully.</p>
          <div className="bg-mist-100 rounded-xl p-6 mt-6 text-left grid grid-cols-2 gap-4">
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Confirmation</p><p className="font-display font-bold text-navy-900" data-testid="confirmation-number">{done.confirmation_number}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Effective date</p><p className="font-display font-bold text-navy-900">{done.effective_date}</p></div>
          </div>
          <div className="flex gap-3 justify-center mt-8">
            <Button onClick={() => navigate("/app/enrollments")} className="bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid="view-coverage">View my coverage</Button>
            <Button onClick={() => navigate("/app/benefits")} variant="outline" className="rounded-full">Continue browsing</Button>
          </div>
        </div>
      </div>
    );
  }

  const canNext = step === 0 ? selectedTier !== null : step === 1 ? true : acknowledged;

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-3xl mx-auto animate-rise">
      <Link to={`/app/product/${id}`} className="inline-flex items-center gap-1 text-sm text-navy-700 hover:text-gold-dark mb-6" data-testid="back-to-product">
        <ArrowLeft className="w-4 h-4" /> Back to plan
      </Link>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < step ? "bg-green-600 text-white" : i === step ? "bg-gold text-navy-900" : "bg-mist-200 text-muted-foreground"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-sm font-semibold hidden sm:block ${i <= step ? "text-navy-900" : "text-muted-foreground"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-green-600" : "bg-mist-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-mist-200 p-8">
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-mist-200">
          <div className="w-12 h-12 rounded-xl bg-navy-800 flex items-center justify-center">
            <BenefitIcon name={product.category?.icon} className="w-6 h-6 text-gold" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{product.carrier?.name}</p>
              <DemoBadge />
            </div>
            <h2 className="font-display text-xl font-bold text-navy-900">{product.name}</h2>
          </div>
        </div>

        {/* Step 0 */}
        {step === 0 && (
          <div data-testid="step-coverage">
            <h3 className="font-display text-lg font-bold text-navy-900 mb-1">Choose your coverage</h3>
            <p className="text-sm text-muted-foreground mb-5">Pick the option that fits your household.</p>
            <RadioGroup value={selectedTier?.toString()} onValueChange={(v) => setSelectedTier(parseInt(v))}>
              <div className="space-y-3">
                {options.map((o, i) => (
                  <label key={i} htmlFor={`tier-${i}`} data-testid={`tier-${i}`}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${selectedTier === i ? "border-gold bg-gold/5" : "border-mist-200 hover:border-mist-300"}`}>
                    <RadioGroupItem value={i.toString()} id={`tier-${i}`} />
                    <div className="flex-1">
                      <p className="font-semibold text-navy-900">{o.label}</p>
                      {o.coverage_amount && <p className="text-xs text-muted-foreground">Benefit amount ${o.coverage_amount.toLocaleString()}</p>}
                    </div>
                    <p className="font-display text-lg font-bold text-navy-900">${(o.monthly_premium ?? 0).toFixed(2)}<span className="text-xs font-sans text-muted-foreground">/mo</span></p>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <div data-testid="step-household">
            <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-gold-dark" /><h3 className="font-display text-lg font-bold text-navy-900">Confirm who's covered</h3></div>
            <p className="text-sm text-muted-foreground mb-5">These household members are eligible for this benefit.</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-mist-100">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                <div><p className="font-semibold text-navy-900 text-sm">{user?.name}</p><p className="text-xs text-muted-foreground">You (always covered)</p></div>
              </div>
              {eligibility?.members?.filter((m) => m.member_id).map((m) => (
                <label key={m.member_id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${!m.eligible ? "opacity-50" : "border-mist-200"}`} data-testid={`cover-${m.member_id}`}>
                  <Checkbox
                    checked={coveredIds.includes(m.member_id)}
                    disabled={!m.eligible}
                    onCheckedChange={(c) => setCoveredIds(c ? [...coveredIds, m.member_id] : coveredIds.filter((x) => x !== m.member_id))}
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-navy-900 text-sm">{m.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{m.relationship.replace("_", " ")}{!m.eligible && m.reasons?.length ? ` · ${m.reasons[0]}` : ""}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div data-testid="step-review">
            <div className="flex items-center gap-2 mb-1"><ClipboardCheck className="w-5 h-5 text-gold-dark" /><h3 className="font-display text-lg font-bold text-navy-900">Review & submit</h3></div>
            <p className="text-sm text-muted-foreground mb-5">Please confirm your election below.</p>
            <div className="rounded-xl bg-mist-100 p-5 space-y-3 text-sm">
              <Row label="Benefit" value={product.name} />
              <Row label="Coverage" value={selectedOption?.label} />
              <Row label="Monthly premium" value={`$${(selectedOption?.monthly_premium ?? 0).toFixed(2)}`} />
              <Row label="Covered members" value={`${1 + coveredIds.length} (incl. you)`} />
            </div>
            <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="acknowledge-checkbox">
              <Checkbox checked={acknowledged} onCheckedChange={setAcknowledged} className="mt-0.5" />
              <span className="text-sm text-muted-foreground">I understand this is a benefit election and I acknowledge the coverage and premium shown above. <span className="font-semibold text-navy-700">(Demo — no real coverage is issued.)</span></span>
            </label>
          </div>
        )}

        {/* Nav */}
        <div className="flex justify-between mt-8 pt-6 border-t border-mist-200">
          <Button variant="outline" className="rounded-full" onClick={() => (step === 0 ? navigate(`/app/product/${id}`) : setStep(step - 1))} data-testid="enroll-back">
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {step < 2 ? (
            <Button disabled={!canNext} onClick={() => setStep(step + 1)} className="bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid="enroll-next">
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button disabled={!canNext || submitting} onClick={submit} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="enroll-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit enrollment"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-navy-900">{value}</span>
    </div>
  );
}
